import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Heart, Hospital, Users, Award } from 'lucide-react';
import LoadPicture from '../../Func/LoadPicture';
import LoadDataHospital from '../../Func/LoadDataHospital';

const NurseTeam = () => {
    const [isGalleryPaused, setIsGalleryPaused] = useState(false);
    const scrollRef = useRef(null);
    const type = 'Nurse'
    const pic = LoadPicture(type)
    const hospital = LoadDataHospital()

    // Auto-scroll logic for Gallery
    useEffect(() => {
        let interval;
        if (!isGalleryPaused && pic && pic.length > 0) {
            interval = setInterval(() => {
                if (scrollRef.current) {
                    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
                    if (scrollLeft + clientWidth >= scrollWidth - 1) {
                        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                    } else {
                        scrollRef.current.scrollBy({ left: 350, behavior: 'smooth' });
                    }
                }
            }, 4000); // Scroll every 4 seconds
        }
        return () => clearInterval(interval);
    }, [isGalleryPaused, pic]);

    return (
        <section className="relative min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 text-slate-900 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-20 left-10 w-96 h-96 bg-orange-200 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            </div>

            {/* Activities Gallery Section */}
            <div className="relative border-y border-orange-100/50 py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8">
                        {/* Header Section - Enhanced */}
                        <div className="space-y-5 flex-1">
                            <div className="inline-flex items-center gap-2 px-4 py-2 text-xs font-black tracking-[0.2em] text-orange-600 uppercase bg-gradient-to-r from-orange-100 to-orange-50 rounded-full border border-orange-200 shadow-sm">
                                <Hospital className="w-4 h-4" />
                                Our Team
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                                ทีมพยาบาล
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-orange-500 to-blue-600">
                                    ผู้เชี่ยวชาญด้านการดูแล
                                </span>
                            </h2>
                            <div className="w-24 h-1.5 bg-gradient-to-r from-orange-600 to-blue-600 rounded-full"></div>
                            <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">
                                สะท้อนมาตรฐานการดูแลที่ได้รับความไว้วางใจ และบรรยากาศการให้บริการที่เป็นกันเองเหมือนครอบครัว 
                                ด้วยทีมพยาบาลผู้เชี่ยวชาญกว่า <span className="font-bold text-orange-600">400 คน</span> ทั่วประเทศ
                            </p>

                            {/* Trust Indicators */}
                            <div className="flex flex-wrap gap-4 pt-4">
                                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border-2 border-blue-100 shadow-sm">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-bold text-slate-700">400+ พยาบาล</span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border-2 border-orange-100 shadow-sm">
                                    <Award className="w-5 h-5 text-orange-600" />
                                    <span className="text-sm font-bold text-slate-700">รับรองมาตรฐาน</span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border-2 border-emerald-100 shadow-sm">
                                    <Heart className="w-5 h-5 text-emerald-600" />
                                    <span className="text-sm font-bold text-slate-700">ดูแลด้วยใจ</span>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Buttons - Enhanced */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => scrollRef.current?.scrollBy({ left: -350, behavior: 'smooth' })}
                                className="group p-4 bg-white border-2 border-orange-200 text-orange-600 rounded-2xl shadow-lg hover:bg-gradient-to-br hover:from-orange-500 hover:to-orange-600 hover:text-white hover:border-orange-500 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 active:scale-95"
                                aria-label="ภาพก่อนหน้า"
                            >
                                <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
                            </button>
                            <button
                                onClick={() => scrollRef.current?.scrollBy({ left: 350, behavior: 'smooth' })}
                                className="group p-4 bg-white border-2 border-blue-200 text-blue-600 rounded-2xl shadow-lg hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-600 hover:text-white hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 active:scale-95"
                                aria-label="ภาพถัดไป"
                            >
                                <ChevronRight className="w-6 h-6" strokeWidth={2.5} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Gallery Content with Horizontal Scroll - Enhanced Cards */}
                <div
                    ref={scrollRef}
                    onMouseEnter={() => setIsGalleryPaused(true)}
                    onMouseLeave={() => setIsGalleryPaused(false)}
                    className="flex gap-6 overflow-x-auto px-4 sm:px-6 lg:px-[calc((100vw-80rem)/2)] pb-12 no-scrollbar snap-x cursor-grab active:cursor-grabbing"
                    style={{ scrollSnapType: 'x mandatory' }}
                >
                    {pic && pic.length > 0 ? (
                        pic.map((item, index) => {
                            const hospitalData = hospital?.find(f => f.hcode === item.split(",")[1]?.split(".")[0]);
                            return (
                                <div
                                    key={`${index + 1}-${index}`}
                                    className="flex-shrink-0 w-[320px] md:w-[450px] group snap-center"
                                >
                                    <div className="relative bg-white p-4 rounded-[2.5rem] shadow-xl shadow-orange-900/10 border-2 border-orange-100 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/30 hover:-translate-y-2 hover:border-orange-300">
                                        {/* Image Container */}
                                        <div className="relative overflow-hidden rounded-[2rem] aspect-[4/3]">
                                            <img
                                                src={item.split(",")[0]}
                                                alt={`ทีมพยาบาล ${hospitalData?.hname || 'ราชพฤกษ์ไตเทียม'}`}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                loading="lazy"
                                            />
                                            
                                            {/* Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                            
                                            {/* Care Badge */}
                                            <div className="absolute top-5 left-5">
                                                <div className="bg-white/95 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-xl border border-orange-100">
                                                    <Heart className="w-4 h-4 text-orange-600 fill-orange-600 animate-pulse" />
                                                    <span className="text-[10px] font-black text-orange-600 uppercase tracking-wider">ดูแลด้วยใจ</span>
                                                </div>
                                            </div>

                                            {/* Quality Badge */}
                                            <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                                <div className="bg-blue-600/95 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-xl">
                                                    <Award className="w-3.5 h-3.5 text-white" />
                                                    <span className="text-[9px] font-black text-white uppercase tracking-wider">Professional</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Info Section - Enhanced */}
                                        <div className="p-6">
                                            <div className="flex items-start gap-2 mb-3">
                                                <MapPin className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <h3 className="text-sm font-black text-slate-900 mb-1 leading-tight">
                                                        {hospitalData?.hname || 'ศูนย์ไตเทียม'}
                                                    </h3>
                                                    <p className="text-xs font-bold text-slate-500">
                                                        {hospitalData?.codeAddress?.split(",")[2] || 'ทั่วประเทศไทย'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Decorative Line */}
                                            <div className="w-full h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent my-4"></div>

                                            {/* Additional Info */}
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-slate-400 font-medium">ทีมมืออาชีพ</span>
                                                <div className="flex gap-1">
                                                    {[...Array(5)].map((_, i) => (
                                                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-orange-400"></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Decorative Corner */}
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-400 to-orange-500 rounded-bl-full opacity-5 group-hover:opacity-10 transition-opacity duration-500"></div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="flex items-center justify-center w-full py-20">
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-100 to-blue-100 animate-pulse"></div>
                                <p className="text-slate-400 font-medium">กำลังโหลดข้อมูล...</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress Indicators */}
                {pic && pic.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 flex justify-center">
                        <div className="flex gap-2">
                            {[...Array(Math.min(pic.length, 8))].map((_, i) => (
                                <div 
                                    key={i} 
                                    className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-300 to-blue-300 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                                ></div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* SEO Content (Hidden but crawlable) */}
            <div className="sr-only">
                <h2>ทีมพยาบาลผู้เชี่ยวชาญ ราชพฤกษ์ไตเทียม</h2>
                <p>
                    ทีมพยาบาลและบุคลากรทางการแพทย์ผู้เชี่ยวชาญกว่า 400 คน 
                    ผ่านการรับรองมาตรฐานจากสมาคมโรคไตแห่งประเทศไทย 
                    พร้อมให้บริการดูแลผู้ป่วยด้วยความเอาใจใส่และเป็นมิตร 
                    ในศูนย์ไตเทียมกว่า 70 แห่งทั่วประเทศไทย
                </p>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </section>
    );
};

export default NurseTeam;
